import { useMemo, useState } from 'react';
import { ChevronDown, ExternalLink, Trophy, Calendar, DollarSign, Clock } from 'lucide-react';

/**
 * TrackEventsAccordion Component
 * 
 * Displays feature racing events for a track in an expandable accordion
 */
const sortOptions = [
  { value: 'date', label: 'Date' },
  { value: 'prize', label: 'Prize money' },
  { value: 'status', label: 'Upcoming vs passed' }
];

const sortDirectionLabels = {
  date: {
    asc: 'Oldest first',
    desc: 'Newest first'
  },
  prize: {
    asc: 'Lowest purse',
    desc: 'Highest purse'
  },
  status: {
    asc: 'Upcoming first',
    desc: 'Finished first'
  }
};

const normalizeRaceNumber = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  if (!str) return null;
  const numeric = parseInt(str, 10);
  if (Number.isFinite(numeric)) {
    return String(numeric);
  }
  return str;
};

const buildRaceKey = (dateKey, raceNumber) => {
  if (!dateKey || !raceNumber) return null;
  return `${dateKey}|${raceNumber}`;
};

const parseEventDateKey = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'object' && value !== null) {
    if (typeof value._seconds === 'number') {
      const millis = (value._seconds * 1000) + (value._nanoseconds ? value._nanoseconds / 1e6 : 0);
      const date = new Date(millis);
      if (!Number.isNaN(date.valueOf())) {
        return date.toISOString().slice(0, 10);
      }
    }
  }

  const str = String(value).trim();
  if (!str) return null;

  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const dmyMatch = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return `${year}-${month}-${day}`;
  }

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.valueOf())) {
    return parsed.toISOString().slice(0, 10);
  }

  return null;
};

export default function TrackEventsAccordion({ events, winningTipsByRace = {} }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');

  if (!events || events.length === 0) {
    return null;
  }

  const enhancedEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (events || []).map((event) => {
      const eventDate = event?.date_2025 ? new Date(event.date_2025) : null;
      const normalisedDate = eventDate
        ? new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate())
        : null;
      const isPast = normalisedDate ? normalisedDate < today : false;
      const raceNumber = normalizeRaceNumber(event?.race_number ?? event?.raceNumber);
      const eventDateKey = parseEventDateKey(event?.date_2025 || event?.date);
      const raceCompositeKey = buildRaceKey(eventDateKey, raceNumber);
      const unknownCompositeKey = raceNumber ? buildRaceKey('unknown', raceNumber) : null;

      let winningTipEntries = [];
      if (raceCompositeKey && Array.isArray(winningTipsByRace?.[raceCompositeKey])) {
        winningTipEntries = [...winningTipsByRace[raceCompositeKey]];
      } else if (unknownCompositeKey && Array.isArray(winningTipsByRace?.[unknownCompositeKey])) {
        winningTipEntries = [...winningTipsByRace[unknownCompositeKey]];
      } else if (raceNumber && Array.isArray(winningTipsByRace?.[raceNumber])) {
        // Fallback for legacy race-number-only keys
        winningTipEntries = [...winningTipsByRace[raceNumber]];
      }

      const rawStatus = typeof event?.status === 'string' ? event.status.trim() : '';
      const formattedStatus = rawStatus
        ? rawStatus.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        : '';

      const statusLabel = formattedStatus || (isPast ? 'Finished' : 'Upcoming');
      const statusTone = formattedStatus
        ? 'bg-purple-100 text-purple-700'
        : isPast
          ? 'bg-gray-200 text-gray-600'
          : 'bg-green-100 text-green-700';

      return {
        ...event,
        eventDate,
        isPast,
        status: isPast ? 'passed' : 'upcoming',
        eventDateKey,
        raceCompositeKey,
        raceNumber,
        winningTipKeys: winningTipEntries,
        statusLabel,
        statusTone
      };
    });
  }, [events, winningTipsByRace]);

  const sortedEvents = useMemo(() => {
    const list = [...enhancedEvents];

    const getTime = (event) => (
      event.eventDate instanceof Date && !Number.isNaN(event.eventDate.valueOf())
        ? event.eventDate.getTime()
        : Number.MAX_SAFE_INTEGER
    );

    list.sort((a, b) => {
      if (sortField === 'date') {
        const timeA = getTime(a);
        const timeB = getTime(b);
        return sortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      }

      if (sortField === 'prize') {
        const prizeA = typeof a.prize_aud === 'number' ? a.prize_aud : 0;
        const prizeB = typeof b.prize_aud === 'number' ? b.prize_aud : 0;
        return sortDirection === 'asc' ? prizeA - prizeB : prizeB - prizeA;
      }

      const statusPriority = sortDirection === 'asc'
        ? { upcoming: 0, passed: 1 }
        : { upcoming: 1, passed: 0 };

      const statusDiff = (statusPriority[a.status] ?? 2) - (statusPriority[b.status] ?? 2);
      if (statusDiff !== 0) {
        return statusDiff;
      }

      const timeA = getTime(a);
      const timeB = getTime(b);
      return timeA - timeB;
    });

    return list;
  }, [enhancedEvents, sortField, sortDirection]);

  const formatPrizeMoney = (amount) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
      return 'TBC';
    }

    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }

    return `$${amount.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = dateString ? new Date(dateString) : null;
    if (!(date instanceof Date) || Number.isNaN(date.valueOf())) {
      return 'Date TBC';
    }

    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#F30099]" />
          <span className="font-medium text-gray-900">Feature Events</span>
          <span className="text-sm text-gray-500">({sortedEvents.length})</span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''
            }`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-gray-200">
          <div className="p-4 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <label htmlFor="feature-events-sort" className="font-medium text-gray-700">
                  Sort by
                </label>
                <select
                  id="feature-events-sort"
                  className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#F30099]/40 focus:border-[#F30099]/60"
                  value={sortField}
                  onChange={(event) => {
                    const nextField = event.target.value;
                    setSortField(nextField);
                    setSortDirection('asc');
                  }}
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                className="self-start inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:border-[#F30099] hover:text-[#F30099] transition-colors"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                {sortDirectionLabels[sortField]?.[sortDirection] ?? 'Ascending'}
              </button>
            </div>

            {sortedEvents.map((event, index) => (
              <div
                key={index}
                className={`flex items-start justify-between gap-4 p-3 rounded-lg border transition-colors ${event.isPast
                  ? 'bg-gray-100 border-gray-200 text-gray-500'
                  : 'bg-gradient-to-r from-gray-50 to-blue-50/30 border-transparent hover:from-gray-100 hover:to-blue-50/50 text-gray-900'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-2">
                      <h4 className="font-semibold text-sm truncate">
                        {event.race}
                      </h4>
                      {event.source && (
                        <a
                          href={event.source}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 text-[#F30099] hover:text-[#F30099]/80 transition-colors"
                          title="View event details"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 self-start rounded-full px-2 py-0.5 text-[11px] font-medium sm:self-auto ${event.statusTone}`}
                    >
                      <Clock className="w-3 h-3" />
                      {event.statusLabel}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(event.date_2025)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      
                      <span className="font-medium text-[#F30099]">
                        {formatPrizeMoney(event.prize_aud)}
                      </span>
                    </div>
                    {event.raceNumber && (
                      <div className="flex items-center gap-1">
                        <span className="rounded bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                          R{event.raceNumber}
                        </span>
                      </div>
                    )}
                  </div>

                  {event.winningTipKeys?.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-amber-600">
                      <Trophy className="w-4 h-4" aria-hidden="true" />
                      <span className="leading-4">
                        {event.winningTipKeys.length === 1
                          ? `Winning tip: ${event.winningTipKeys[0]}`
                          : `Winning tips: ${event.winningTipKeys.join(' · ')}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
